import { ERROR_MESSAGES } from './cms-api-error';


export const errorMess = {
  "1001": "not login",
}

export const NotLoginCode = 1001;

// BFF（cms-client-admin の ApiProxy）が未ログイン・ログイン切れの 401 で返す error のキー。
export const NOT_LOGIN_ERROR_KEY = 'not_login';

export const SESSION_EXPIRED_MESSAGE = 'ログインの有効期限が切れました。再ログインしてください。';

/**
 * 応答の error（{ キー: 内容 }）が未ログインか。
 *
 * ⚠ 以前は `error instanceof Map` と U000053 / U000054 / U000055 / U000072 / U000045 を見ていた。
 *   JSON の error は普通のオブジェクトなので常に false で、応答からの未ログイン判定は効いていなかった。
 *   しかもこれらはデータ入出力・ファイル・プレビューのトークンのエラーで、ログインとは無関係
 *   （ここで拾うと、プレビューのトークン不足などで「ログイン切れ」と誤って案内してしまう）。
 */
export const ApiIsLoginError = (error) => {
  if (error instanceof Map) return error.has(NOT_LOGIN_ERROR_KEY);
  return !!error && typeof error === 'object'
    && Object.prototype.hasOwnProperty.call(error, NOT_LOGIN_ERROR_KEY);
}

/** 応答本文（{ status, error }）が未ログインか。cms-axios のインターセプタは本文をそのまま投げる。 */
export const isNotLoginBody = (body) =>
  !!body && typeof body === 'object' && ApiIsLoginError(body.error);

class ApiError extends Error {

  ErrorCode = 0;

  constructor(message) {
    super(message);
    this.name = "Api";
  }
}

export default ApiError;

// ----------------------------------------------------------------------
// API エラーの正規化
//
// cms-axios.js のインターセプタは HTTP 400 のとき response.data をそのまま reject する。
// つまり catch に来る値は Error とは限らず、形も一定しない:
//   {status:'USER_ERROR', error:{Name:'...', ItemName:'...'}}  バリデーションのフィールドマップ
//   {status:'USER_ERROR', error:'...'}                          文字列
//   {status:'USER_ERROR', error:{U000033:{...}}}                入れ子
//   Error('...')                                                httpGet/httpPost が status!=='OK' で投げる分
//   '...'                                                       素の文字列
//
// `error?.message` だけを見ると 400 系は必ず undefined になり、どの画面も
// 「保存に失敗しました」しか出せない。サーバーがどのフィールドが悪いか言っているのに捨てている。

/**
 * USER_ERROR のフィールドエラーを表示できる文字列の配列にする。
 * インラインで一覧表示したいとき（<Alert> を並べる等）に使う。
 *
 * @param {*} error reject された値
 * @returns {string[]} USER_ERROR 以外や中身が無ければ空配列
 */
export function normalizeApiErrors(error) {
    if (!error || error.status !== 'USER_ERROR') return [];

    const detail = error.error;
    if (!detail) return [];

    if (typeof detail === 'string') return [detail];

    if (Array.isArray(detail)) {
        return detail.map((v) => (typeof v === 'string' ? v : JSON.stringify(v)));
    }

    if (typeof detail === 'object') {
        return Object.keys(detail).map((key) => {
            const val = detail[key];
            // 後端のメッセージは英語。日本語を持つコードはコードから引く。
            if (typeof val === 'string') return ERROR_MESSAGES[key] || val;
            return `${key}: ${JSON.stringify(val)}`;
        });
    }

    return [String(detail)];
}

/**
 * スナックバー 1 行に出すためのメッセージを返す。
 * USER_ERROR ならサーバーが返した内容を出し、それ以外は Error.message、
 * どれでもなければ呼び出し側の既定文言に落とす。
 *
 * @param {*} error reject された値
 * @param {string} fallback 既定の文言（例 '保存に失敗しました'）
 * @returns {string}
 */
export function getApiErrorMessage(error, fallback) {
    // ログイン切れ。既定文言（「保存に失敗しました」等）に落とすと原因が判らない。
    if (isNotLoginBody(error) || error?.ErrorCode === NotLoginCode) return SESSION_EXPIRED_MESSAGE;

    // cms-api-error.js が日本語文言を持つコード（E000011 / U000066 / U000144 / U000145 / TIMEOUT）は
    // 英語の原文（normalizeApiErrors が拾う）より message を優先する。
    if (error?.code && ERROR_MESSAGES[error.code] && error.message) return error.message;

    const messages = normalizeApiErrors(error);
    if (messages.length > 0) return messages.join(' / ');

    if (typeof error === 'string' && error) return error;
    if (error?.message) return error.message;

    return fallback;
}
